import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, Avatar, Chip, Typography, Dialog, DialogTitle, DialogContent, IconButton, TextField, InputAdornment, List, ListItemAvatar, ListItemText, ListItemButton, Skeleton, Stack } from '@mui/material';
import { CustomBreadcrumbs, PageBanner, KpiCard, TabMenu } from '../../common';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import teacherBannerBg from '../../../assets/images/teacher.jpg';

import UpcomingExamsTab from './UpcomingExamsTab';
import GradedExamsTab from './GradedExamsTab';
import TeacherSelection from './TeacherSelection';
import teacherService from '../../../infrastructure/services/teacherService';

const getProfileImageUrl = (pic) => {
  if (!pic) return null;
  if (pic.startsWith('http://') || pic.startsWith('https://')) {
    return pic;
  }
  return `https://fonixedugrading.blob.core.windows.net/eduone-auth-content/${pic}`;
};

const getInitials = (name) => {
  if (!name) return 'T';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export default function ExamManagerPage() {
  const navigate = useNavigate();
  const { teacherId, activeTab: tabFromUrl } = useParams();
  const activeTab = tabFromUrl === 'history' ? 'history' : 'current';
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kpiStats, setKpiStats] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const handleNotification = (event) => {
      if (event?.detail?.type === 'exam_submission_failed') {
        setRefreshTrigger(prev => prev + 1);
      }
    };
    window.addEventListener('notification:received', handleNotification);
    return () => {
      window.removeEventListener('notification:received', handleNotification);
    };
  }, []);

  const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);
  const [modalTeachers, setModalTeachers] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalSearch, setModalSearch] = useState('');
  const [switchingTeacher, setSwitchingTeacher] = useState(null);

  const handleOpenChangeModal = async () => {
    setIsChangeModalOpen(true);
    setModalLoading(true);
    try {
      const data = await teacherService.getTeachers({
        search: '',
        sectionId: 'exam',
        page: 1,
        pageSize: 100
      });
      if (data && data.isSuccessful) {
        setModalTeachers(data.listContent || []);
      }
    } catch (err) {
      console.error('Failed to load modal teachers:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseChangeModal = () => {
    if (switchingTeacher) return;
    setIsChangeModalOpen(false);
    setModalSearch('');
  };

  const handleSelectTeacher = (teacher) => {
    if (teacher.teacherId === teacherId) {
      setIsChangeModalOpen(false);
      return;
    }
    setSwitchingTeacher(teacher);
    navigate(`/exam-manager/exams/${teacher.teacherId}/${activeTab}`);
  };

  const handleStatsFetched = useCallback((stats) => {
    setKpiStats(stats);
  }, []);

  useEffect(() => {
    if (!teacherId) {
      setSelectedTeacher(null);
      setLoading(false);
      return;
    }

    const loadTeacherDetails = async () => {
      try {
        setLoading(true);
        const data = await teacherService.getTeachers({
          search: '',
          sectionId: 'exam',
          page: 1,
          pageSize: 100
        });
        if (data && data.isSuccessful) {
          const found = data.listContent?.find((t) => t.teacherId === teacherId);
          if (found) {
            setSelectedTeacher(found);
          } else {
            setSelectedTeacher(null);
          }
        } else {
          setSelectedTeacher(null);
        }
      } catch (err) {
        console.error('Failed to load teacher details:', err);
        setSelectedTeacher(null);
      } finally {
        setLoading(false);
        setSwitchingTeacher(null);
        setIsChangeModalOpen(false);
      }
    };

    loadTeacherDetails();
  }, [teacherId]);

  if (loading && switchingTeacher) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
          fontFamily: 'Poppins, sans-serif',
          textAlign: 'center',
          gap: 3.5
        }}
      >
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress
            size={90}
            thickness={3}
            sx={{ color: '#FF502D', position: 'absolute' }}
          />
          <Avatar
            src={getProfileImageUrl(switchingTeacher.profilePicture) || undefined}
            sx={{
              width: 70,
              height: 70,
              border: '2.5px solid #ff502d',
              boxShadow: '0 8px 24px rgba(255, 80, 45, 0.15)',
              bgcolor: '#ffffff',
              color: '#ff502d',
              fontSize: '24px',
              fontWeight: 600,
              fontFamily: 'Poppins'
            }}
          >
            {getInitials(switchingTeacher.teacherName)}
          </Avatar>
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#1e293b', fontFamily: 'Poppins' }}>
            Switching to {switchingTeacher.teacherName}'s Exams
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#94a3b8', fontFamily: 'Poppins', mt: 0.5 }}>
            Loading assigned exams and graded results...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ pb: 5, fontFamily: 'Poppins, sans-serif' }}>
        {/* Breadcrumbs Skeleton */}
        <Box sx={{ mb: 2.5, width: 200 }}>
          <Skeleton variant="text" width="60%" height={24} />
        </Box>

        {/* Modern Compact Profile Header Row Skeleton */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: '24px',
            border: '1.5px solid #cbd5e1',
            background: '#ffffff',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, width: '100%' }}>
            <Skeleton variant="circular" width={76} height={76} />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Skeleton variant="text" width={180} height={32} />
                <Skeleton variant="rectangular" width={120} height={26} sx={{ borderRadius: '20px' }} />
              </Box>
              <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
            </Box>
          </Box>
        </Paper>

        {/* KPI Cards Row Skeleton */}
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' },
            mb: 4
          }}
        >
          {Array.from({ length: 5 }).map((_, idx) => (
            <Paper
              key={idx}
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: '20px',
                border: '1.5px solid #cbd5e1',
                height: 120,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Skeleton variant="text" width="50%" height={20} />
              <Skeleton variant="text" width="30%" height={40} />
              <Skeleton variant="text" width="40%" height={16} />
            </Paper>
          ))}
        </Box>

        {/* Content Skeleton */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: '1.5px solid #cbd5e1',
            borderRadius: '24px'
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: '8px' }} />
            <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: '8px' }} />
          </Box>
          <Stack spacing={2}>
            {Array.from({ length: 3 }).map((_, idx) => (
              <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                <Box sx={{ flex: 1, mr: 4 }}>
                  <Skeleton variant="text" width="70%" height={24} />
                  <Skeleton variant="text" width="40%" height={16} sx={{ mt: 1 }} />
                </Box>
                <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: '8px' }} />
              </Box>
            ))}
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (!selectedTeacher) {
    return <TeacherSelection />;
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/dashboard' },
    { label: 'Exam Management', onClick: () => navigate('/exam-manager'), style: { cursor: 'pointer' } },
    { label: selectedTeacher.teacherName }
  ];

  const totalExams = kpiStats ? kpiStats.total : selectedTeacher.totalExamCount;
  const completedCount = kpiStats ? kpiStats.completed : Math.max(0, selectedTeacher.totalExamCount - selectedTeacher.upcomingExamCount);
  const ongoingCount = kpiStats ? kpiStats.ongoing : 0;
  const upcomingCount = kpiStats ? kpiStats.upcoming : selectedTeacher.upcomingExamCount;
  const missedCount = kpiStats ? kpiStats.missed : 0;

  const kpis = [
    {
      title: 'Assigned Exams',
      value: totalExams.toString(),
      change: 'Active in courses',
      timeframe: '',
      borderColor: '#5CA600',
      bgGradient: 'linear-gradient(180deg, rgba(152, 255, 100, 0.15) 0%, rgba(255, 255, 255, 0.15) 93.95%)'
    },
    {
      title: 'Ongoing Exams',
      value: ongoingCount.toString(),
      change: 'Active test sessions',
      timeframe: '',
      borderColor: '#3B82F6',
      bgGradient: 'linear-gradient(180deg, rgba(59, 130, 246, 0.15) 0%, rgba(255, 255, 255, 0.15) 93.95%)'
    },
    {
      title: 'Upcoming Exams',
      value: upcomingCount.toString(),
      change: 'Scheduled tests',
      timeframe: '',
      borderColor: '#FFA175',
      bgGradient: 'linear-gradient(180deg, rgba(255, 161, 117, 0.15) 0%, rgba(255, 255, 255, 0.15) 93.95%)'
    },
    {
      title: 'Completed Exams',
      value: completedCount.toString(),
      change: 'Graded & reviewed',
      timeframe: '',
      borderColor: '#00B692',
      bgGradient: 'linear-gradient(180deg, rgba(100, 255, 224, 0.15) 0%, rgba(255, 255, 255, 0.15) 93.95%)'
    },
    {
      title: 'Missed Exams',
      value: missedCount.toString(),
      change: missedCount > 0 ? `${missedCount} missed exams` : 'No missed exams',
      timeframe: '',
      borderColor: '#ff502d',
      bgGradient: 'linear-gradient(180deg, rgba(255, 80, 45, 0.15) 0%, rgba(255, 255, 255, 0.15) 93.95%)'
    }
  ];

  const primarySubject = selectedTeacher.subjects?.[0] || '';

  const filteredModalTeachers = modalTeachers.filter((t) =>
    t.teacherName?.toLowerCase().includes(modalSearch.toLowerCase()) ||
    t.subjects?.some((s) => s.toLowerCase().includes(modalSearch.toLowerCase()))
  );

  const activeTeacher = filteredModalTeachers.find((t) => t.teacherId === teacherId);
  const otherTeachers = filteredModalTeachers.filter((t) => t.teacherId !== teacherId);
  const sortedModalTeachers = activeTeacher ? [activeTeacher, ...otherTeachers] : filteredModalTeachers;

  return (
    <Box sx={{ pb: 5, fontFamily: 'Poppins, sans-serif' }}>
      {/* Breadcrumbs */}
      <CustomBreadcrumbs items={breadcrumbItems} sx={{ mb: 2.5 }} />

      {/* Modern Compact Profile Header Row using PageBanner */}
      <PageBanner
        backgroundImage={teacherBannerBg}
        title={
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 2.5, textAlign: 'left' }}>
            <Avatar
              src={getProfileImageUrl(selectedTeacher.profilePicture) || undefined}
              sx={{
                width: { xs: 56, sm: 68 },
                height: { xs: 56, sm: 68 },
                border: '2.5px solid #ffffff',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                bgcolor: '#ffffff',
                color: '#ff502d',
                fontWeight: 700,
                fontSize: { xs: '18px', sm: '22px' },
                fontFamily: 'Poppins',
                flexShrink: 0
              }}
            >
              {getInitials(selectedTeacher.teacherName)}
            </Avatar>
            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 0.5 }}>
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '18px', sm: '22px' },
                    fontFamily: 'Poppins',
                    color: '#ffffff',
                    lineHeight: 1.2
                  }}
                >
                  {selectedTeacher.teacherName}
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleOpenChangeModal}
                  startIcon={<CompareArrowsRoundedIcon sx={{ fontSize: '13px !important' }} />}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    color: '#ffffff',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '11px',
                    fontFamily: 'Poppins',
                    borderRadius: '20px',
                    px: 1.5,
                    py: 0.25,
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(10px)',
                    height: '24px',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)',
                      borderColor: 'rgba(255, 255, 255, 0.5)'
                    }
                  }}
                >
                  Change Teacher
                </Button>
              </Box>
              <Typography
                component="span"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: { xs: '11.5px', sm: '13px' },
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  lineHeight: 1.4,
                  textAlign: 'left'
                }}
              >
                Monitor active schedules and test papers
              </Typography>
            </Box>
          </Box>
        }
      >
        <TabMenu
          tabs={[
            { id: 'current', label: 'Current', icon: <CalendarMonthRoundedIcon /> },
            { id: 'history', label: 'History', icon: <AssignmentTurnedInRoundedIcon /> }
          ]}
          activeTab={activeTab}
          onChange={(tabId) => navigate(`/exam-manager/exams/${teacherId}/${tabId}`)}
        />
      </PageBanner>

      {/* KPI Cards Row */}
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' },
          mb: 4
        }}
      >
        {kpis.map((kpi, idx) => (
          <KpiCard
            key={idx}
            title={kpi.title}
            value={kpi.value}
            footerText={kpi.change}
            borderColor={kpi.borderColor}
            bgGradient={kpi.bgGradient}
          />
        ))}
      </Box>

      {/* Tabs Content */}
      <Box sx={{ mt: 2 }}>
        {activeTab === 'current' ? (
          <UpcomingExamsTab
            subjectFilter={primarySubject}
            teacherName={selectedTeacher.teacherName}
            onStatsFetched={handleStatsFetched}
            refreshTrigger={refreshTrigger}
          />
        ) : (
          <GradedExamsTab
            subjectFilter={primarySubject}
            teacherName={selectedTeacher.teacherName}
            onStatsFetched={handleStatsFetched}
            refreshTrigger={refreshTrigger}
          />
        )}
      </Box>

      {/* Change Teacher Modal */}
      <Dialog
        open={isChangeModalOpen}
        onClose={handleCloseChangeModal}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '32px !important',
            p: 1,
            border: '1.5px solid #e2e8f0',
            boxShadow: '0 24px 64px rgba(0,0,0,0.08)',
            fontFamily: 'Poppins, sans-serif',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1.5,
            pt: 2.5,
            px: 3
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#1e293b', fontFamily: 'Poppins' }}>
              Change Teacher
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'Poppins', mt: 0.5 }}>
              Select a teacher to switch exams and view schedules
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseChangeModal}
            size="small"
            disabled={!!switchingTeacher}
            sx={{
              color: '#64748b',
              bgcolor: '#f1f5f9',
              '&:hover': { bgcolor: '#e2e8f0', color: '#0f172a' },
              transition: 'all 0.2s'
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 3.5 }}>
          {/* Search Field */}
          <TextField
            fullWidth
            placeholder="Search teacher or subject..."
            size="small"
            value={modalSearch}
            disabled={!!switchingTeacher}
            onChange={(e) => setModalSearch(e.target.value)}
            sx={{
              mb: 3,
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '16px',
                fontFamily: 'Poppins',
                fontSize: '13px',
                bgcolor: '#f8fafc',
                '& fieldset': { borderColor: '#cbd5e1' },
                '&:hover fieldset': { borderColor: '#94a3b8' },
                '&.Mui-focused fieldset': { borderColor: '#64748b' }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: '#94a3b8', fontSize: '18px' }} />
                </InputAdornment>
              )
            }}
          />

          {/* Teacher List */}
          {modalLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#FF502D' }} />
            </Box>
          ) : sortedModalTeachers.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography sx={{ color: '#94a3b8', fontSize: '13px', fontFamily: 'Poppins' }}>
                No teachers found matching your search.
              </Typography>
            </Box>
          ) : (
            <List
              sx={{
                maxHeight: 340,
                overflowY: 'auto',
                pr: 1,
                '&::-webkit-scrollbar': {
                  width: '6px'
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f5f9',
                  borderRadius: '4px'
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#cbd5e1',
                  borderRadius: '4px'
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#94a3b8'
                }
              }}
            >
              {sortedModalTeachers.map((t) => {
                const isActive = t.teacherId === teacherId;
                return (
                  <ListItemButton
                    key={t.teacherId}
                    disabled={!!switchingTeacher}
                    onClick={() => handleSelectTeacher(t)}
                    sx={{
                      borderRadius: '16px',
                      mb: 1.5,
                      border: isActive ? '2px solid #ff502d' : '1.5px solid #f1f5f9',
                      bgcolor: isActive ? '#fffbf9' : '#ffffff',
                      p: 1.75,
                      boxShadow: isActive ? '0 4px 12px rgba(255, 80, 45, 0.06)' : 'none',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: isActive ? '#fff7f4' : '#f8fafc',
                        borderColor: isActive ? '#ff502d' : '#cbd5e1',
                        boxShadow: isActive ? '0 4px 16px rgba(255, 80, 45, 0.08)' : '0 2px 8px rgba(0,0,0,0.02)'
                      }
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 52 }}>
                      <Avatar
                        src={getProfileImageUrl(t.profilePicture) || undefined}
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: '#f1f5f9',
                          color: '#64748b',
                          fontWeight: 600,
                          fontSize: '14px',
                          border: isActive ? '2px solid #ff502d' : '1.5px solid #cbd5e1',
                          fontFamily: 'Poppins'
                        }}
                      >
                        {getInitials(t.teacherName)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '13.5px', color: '#0f172a', fontFamily: 'Poppins' }}>
                            {t.teacherName}
                          </Typography>
                          {isActive && (
                            <Chip
                              label="Active"
                              size="small"
                              sx={{
                                bgcolor: '#DCFCE7',
                                color: '#15803D',
                                fontWeight: 600,
                                fontSize: '10px',
                                height: '18px',
                                fontFamily: 'Poppins'
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {t.subjects && t.subjects.length > 0 ? (
                            t.subjects.map((subj, idx) => (
                              <Chip
                                key={idx}
                                label={subj}
                                size="small"
                                sx={{
                                  fontSize: '9px',
                                  height: '16px',
                                  bgcolor: 'rgba(255, 80, 45, 0.04)',
                                  color: '#ff502d',
                                  fontFamily: 'Poppins',
                                  fontWeight: 500,
                                  border: '1px solid rgba(255, 80, 45, 0.08)'
                                }}
                              />
                            ))
                          ) : (
                            <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic', fontFamily: 'Poppins' }}>
                              No subjects
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </DialogContent>
      </Dialog>

    </Box>
  );
}

